// Precise New York Stock Exchange regular trading hours (9:30 AM – 4:00 PM ET).
//
// Distinct from the C# API's `IsInUsRegularTradingSession`, which uses a
// 13:30-21:00 UTC superset window covering both EDT and EST to avoid per-day
// Eastern Time conversion. The wide window is correct for *most* integrity
// checks (the boundary minutes don't matter), but for **freshness gates with
// tight 5-min thresholds** the wide window misclassifies the 20:00-21:00 UTC
// hour on EDT days as RTH when it's actually after-hours — basket bars stop
// flowing post-close, but the wide-window predicate keeps inUsSession=true,
// firing false FAILs on K_t intraday + Databento basket equity checks.
//
// Same probe-both-candidate-hours pattern as `ukHourUtc` (ukStrike.ts):
// iOS Safari quirks broke naive `Intl.DateTimeFormat('en-US', { hour12:
// false })` offset math by returning 12-hour values silently. Using `en-US`
// + `hourCycle: 'h23'` + `formatToParts` sidesteps that entirely.
//
// Caller convention: passes a UTC `Date`; returns true iff that instant is
// inside 9:30 AM – 4:00 PM US Eastern Time on a weekday. Holiday detection
// is NOT done here — call sites combine this with `isUsTradingDay` from
// the API for full coverage.
export function isInActualEtRth(now: Date = new Date()): boolean {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hourCycle: 'h23',
      hour: 'numeric',
      minute: 'numeric',
      weekday: 'short',
    })
    const parts = fmt.formatToParts(now)
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? ''

    const wd = get('weekday')
    if (wd === 'Sat' || wd === 'Sun') return false

    const hour = parseInt(get('hour'), 10)
    const minute = parseInt(get('minute'), 10)
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return false

    const minutesSinceMidnight = hour * 60 + minute
    // 9:30 AM ET (= 570) inclusive, 4:00 PM ET (= 960) exclusive.
    return minutesSinceMidnight >= 570 && minutesSinceMidnight < 960
  } catch {
    return false
  }
}

// Stale session-anchor gate (2026-09-24, paolo): the US Eastern calendar
// date a UTC instant falls on — i.e. which session a print belongs to.
//
// Compared against the enriched payload's per-holding session_close_date to
// tell "after-hours drift beyond the last close" from "a whole session has
// closed since these anchors were read". UTC dates cannot do this job: a
// post-20:00-ET print rolls past midnight UTC while still belonging to the
// session that just closed, which would read as a session boundary that
// isn't there.
//
// Same en-US + hourCycle h23 + formatToParts pattern as isInActualEtRth
// above, for the same iOS Safari reason. Returns null rather than throwing
// on an invalid Date so call sites can treat "unknown" as "don't gate".
export function etDateOf(instant: Date): string | null {
  try {
    if (Number.isNaN(instant.getTime())) return null
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(instant)
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? ''
    const y = get('year'), m = get('month'), d = get('day')
    if (!y || !m || !d) return null
    return `${y}-${m}-${d}`
  } catch {
    return null
  }
}

// Minutes since ET midnight for a UTC instant, or null if unparseable.
// Same en-US + hourCycle h23 + formatToParts pattern as the two above.
function etMinutesOf(instant: Date): number | null {
  try {
    if (Number.isNaN(instant.getTime())) return null
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hourCycle: 'h23',
      hour: 'numeric',
      minute: 'numeric',
    }).formatToParts(instant)
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? ''
    const h = parseInt(get('hour'), 10), m = parseInt(get('minute'), 10)
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null
    return h * 60 + m
  } catch {
    return null
  }
}

const ET_RTH_OPEN_MIN = 9 * 60 + 30  // 09:30 ET
const ET_RTH_CLOSE_MIN = 16 * 60     // 16:00 ET

// Stale session-anchor gate (2026-09-24, paolo; pre-market case 2026-09-25).
//
// True iff a whole US session has CLOSED since `sessionCloseDate` — i.e. the
// session_close/session_prev_close pair the enriched payload carries no
// longer describes the last completed session, so `cur/session_close - 1`
// has stopped being after-hours drift and has swallowed an entire regular
// session. Observed 2026-09-24: GGE's anchors were the 09-22 pair while the
// 09-23 session had already closed, so XOM showed mkt +0.26% and ah +1.42%
// when the session it had traded through actually closed +1.59%.
//
// A later ET calendar date on the print is NOT sufficient on its own — that
// was the first cut of this gate and it false-positived twice:
//
//   * Pre-market. A 04:25 ET print on D+1 sits on a later ET date while D's
//     close is still the correct anchor: that IS after-hours drift and must
//     decompose normally. Measured against live Redis on 2026-09-25 05:56
//     ET, the date-only rule suppressed VLO, CVX, BP and IMO — VLO's real
//     mkt +1.89% (the 09-24 session) and ah -1.00% (genuine pre-market
//     drift off a 379.51 print vs a 382.94 close), both blanked. The count
//     grows through the UK morning as more US names pick up pre-market
//     prints, so most US rows would have gone blank by lunchtime.
//   * Live RTH. During the D session the payload legitimately still carries
//     D-1's anchors (it only rebuilds at the close + 15min), so every US row
//     would have blanked for the whole 14:30-21:00 UK window.
//
// Hence the three-part test: the print is on a later ET session date, it is
// an RTH-or-later print rather than pre-market, AND that session has since
// closed. `now` is injectable so the tests can pin the clock. Anything
// unknown returns false — an unreadable timestamp must not blank a row.
//
// Deliberately no US holiday calendar (same limitation as isInActualEtRth).
// It cannot produce a false positive here: the gate needs a real print on
// the later session, and a closed market produces none.
export function isSessionAnchorOvertaken(
  lastRealAtUtc: string | null | undefined,
  sessionCloseDate: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!lastRealAtUtc || !sessionCloseDate) return false

  const print = new Date(lastRealAtUtc)
  const printDate = etDateOf(print)
  if (printDate == null || printDate <= sessionCloseDate) return false

  // Pre-market prints belong to a session that has OPENED, not one that has
  // closed — the prior close is still the right anchor.
  const printMin = etMinutesOf(print)
  if (printMin == null || printMin < ET_RTH_OPEN_MIN) return false

  // ...and that session must actually be over. The feed stamps its last real
  // print at 15:59 ET, so this cannot key off the print time alone.
  const nowDate = etDateOf(now)
  if (nowDate == null) return false
  if (nowDate > printDate) return true
  if (nowDate < printDate) return false
  const nowMin = etMinutesOf(now)
  return nowMin != null && nowMin >= ET_RTH_CLOSE_MIN
}
